/**
 * Drei Lagen statt frei/belegt.
 *
 * "Belegt" oder "frei" ist zu grob. Eine Maschine, die am Dienstag zur UVV
 * muss und den Rest der Woche frei ist, ist nicht belegt -- und wer sie so
 * liest, vermietet sie nicht, obwohl er koennte. Darum:
 *
 *   geht   im Fenster ist nichts Hartes drauf
 *   teils  ein Teil der Tage ist vergeben, der Rest ist zu haben
 *   nein   jeder Tag des Fensters ist hart vergeben
 *
 * Seit dem 16.09.2026 gibt es einen zweiten Grund, warum ein Tag nicht zu
 * haben ist: Das Geraet ist an diesem Tag gar nicht da. Fremdgeraete sind
 * nur so lange unser, wie der Anmietvertrag laeuft (Migration 0080) --
 * `Geraetefenster` traegt diese Grenze herein. Sie rechnet wie eine
 * Belegung und zaehlt getrennt, weil man sie nicht verschieben kann.
 *
 * `'teils'` ist der haeufigste Fall und war im alten Entwurf nicht vorgesehen.
 *
 * Optionen (`art = 'option'`) blocken nicht hart, sie warnen: Wer zuerst
 * zusagt, bekommt die Maschine. Das ist dieselbe Trennung wie im
 * Ausschluss-Constraint der Datenbank, der 'option' ausspart.
 */
import { tagNummer, ausTagNummer, zaehltAlsMiettag } from "./kalender.js";
import { alsIso } from "./typen.js";
/** Die harten Belegungsarten -- dieselben wie im Ausschluss-Constraint von `booking`. */
export const HARTE_ARTEN = [
    'fest',
    'laeuft',
    'wartung',
    'transport',
    'sperre',
    'urlaub',
    'krank',
    'schulung',
    // Der Termin einer Ortsaufnahme (Migration 0180) sperrt die Person wie eine Schulung.
    'termin',
];
/**
 * Welche Tage des Fensters zaehlen ueberhaupt?
 *
 * Bei fuenf Kalendertagen sind Samstag und Sonntag keine Betriebstage. Eine
 * Sperre, die nur auf einem Sonntag liegt, macht eine Woche nicht "teils" --
 * am Sonntag faehrt niemand hin.
 *
 * ANNAHME: Die Doku sagt fuer die Verfuegbarkeit nichts ueber den
 * Mietkalender. Hier wird er angewendet, weil sonst jede Wochenendwartung
 * eine gelbe Lage erzeugt.
 */
function fenstertage(von, bis, regeln) {
    const ersterTag = tagNummer(von);
    const letzterTag = tagNummer(bis);
    const tage = [];
    for (let n = ersterTag; n <= letzterTag; n++) {
        if (zaehltAlsMiettag(n, regeln))
            tage.push(n);
    }
    return tage;
}
/** Zusammenhaengende Strecken aus einer aufsteigenden Tagesliste. */
function strecken(tage) {
    const ergebnis = [];
    for (const n of tage) {
        const letzte = ergebnis[ergebnis.length - 1];
        if (letzte !== undefined && n === letzte.bis + 1)
            letzte.bis = n;
        else
            ergebnis.push({ von: n, bis: n });
    }
    return ergebnis;
}
function istHart(belegung) {
    if (belegung.art === 'option')
        return false;
    return belegung.fest;
}
/**
 * Die Lage einer Ressource im gefragten Fenster.
 *
 * Reine Funktion: dieselben Belegungen liefern dieselbe Antwort, egal wann
 * gefragt wird. Kein `new Date()`, keine Zeitzone.
 */
export function lageBestimmen(eingabe) {
    const fenster = fenstertage(eingabe.von, eingabe.bis, eingabe.regeln);
    const warum = [];
    if (fenster.length === 0) {
        // Ein Fenster, das nur aus Wochenende besteht (oder rueckwaerts laeuft).
        return {
            lage: 'geht',
            text_key: 'lage.kein_betriebstag',
            punkt: 'gut',
            tage_vergeben: 0,
            tage_ausserhalb: 0,
            rand: null,
            warum: [{ key: 'lage.kein_betriebstag', kalendertage: eingabe.regeln.kalendertage }],
        };
    }
    const imFenster = new Set(fenster);
    const hart = eingabe.belegungen.filter(istHart);
    const weich = eingabe.belegungen.filter((b) => !istHart(b));
    const vergeben = new Set();
    for (const belegung of hart) {
        const von = tagNummer(belegung.von);
        const bis = tagNummer(belegung.bis);
        for (let n = von; n <= bis; n++) {
            if (imFenster.has(n))
                vergeben.add(n);
        }
    }
    /* Die Tage, an denen es das Geraet nicht gibt.
       Sie werden wie vergebene Tage behandelt -- dieselbe Mechanik, damit
       "teils" auch dann herauskommt, wenn ein Fremdgeraet mitten in der
       angefragten Woche zurueckgeht. Sie zaehlen aber getrennt, weil sie
       etwas anderes bedeuten: Eine Belegung laesst sich verschieben, ein
       Rueckgabetermin nicht. */
    const ausserhalb = new Set();
    if (eingabe.fenster) {
        const ab = eingabe.fenster.ab === null ? null : tagNummer(eingabe.fenster.ab);
        const zu = eingabe.fenster.bis === null ? null : tagNummer(eingabe.fenster.bis);
        for (const n of fenster) {
            if ((ab !== null && n < ab) || (zu !== null && n > zu))
                ausserhalb.add(n);
        }
    }
    const belegteArten = [...new Set(hart.map((b) => b.art))].sort();
    const freieTage = fenster.filter((n) => !vergeben.has(n) && !ausserhalb.has(n));
    const freieStrecken = strecken(freieTage);
    if (ausserhalb.size > 0) {
        // Die Auskunft nennt die Raender, nicht nur die Zahl: "wir haben ihn erst
        // ab dem 14.10." ist ein Satz, "drei Tage fehlen" ist keiner.
        warum.push({
            key: 'lage.ausserhalb',
            tage: ausserhalb.size,
            ab: eingabe.fenster?.ab ? alsIso(eingabe.fenster.ab) : null,
            bis: eingabe.fenster?.bis ? alsIso(eingabe.fenster.bis) : null,
        });
    }
    for (const strecke of freieStrecken) {
        warum.push({
            key: 'lage.frei_fenster',
            von: alsIso(ausTagNummer(strecke.von)),
            bis: alsIso(ausTagNummer(strecke.bis)),
            tage: strecke.bis - strecke.von + 1,
        });
    }
    const rand = freieStrecken.length === 0
        ? null
        : `${alsIso(ausTagNummer(freieStrecken[0].von))}/${alsIso(ausTagNummer(freieStrecken[0].bis))}`;
    if (freieTage.length === 0) {
        /* Nichts zu holen. Welcher der beiden Gruende genannt wird, entscheidet
           sich danach, was der Fragende tun kann: Steht nur das Fenster im Weg,
           hilft kein Umdisponieren -- dann heisst es "haben wir in dieser Woche
           nicht". Liegt auch nur ein Tag an einer Belegung, ist "belegt" die
           Auskunft, die zum Telefonhoerer fuehrt. */
        if (vergeben.size === 0) {
            // Kein `unshift`: Der Schritt mit den Raendern steht oben schon, und
            // zweimal derselbe Schluessel liest sich wie zwei Gruende.
            return {
                lage: 'nein',
                text_key: 'lage.ausserhalb',
                punkt: 'stopp',
                tage_vergeben: 0,
                tage_ausserhalb: ausserhalb.size,
                rand: null,
                warum,
            };
        }
        warum.unshift({ key: 'lage.belegt', tage: vergeben.size, arten: belegteArten });
        return {
            lage: 'nein',
            text_key: 'lage.belegt',
            punkt: 'stopp',
            tage_vergeben: vergeben.size,
            tage_ausserhalb: ausserhalb.size,
            rand: null,
            warum,
        };
    }
    if (vergeben.size > 0 || ausserhalb.size > 0) {
        warum.unshift({
            key: 'lage.teilweise',
            tage_vergeben: vergeben.size,
            tage_ausserhalb: ausserhalb.size,
            tage_fenster: fenster.length,
            arten: belegteArten,
        });
        return {
            lage: 'teils',
            text_key: 'lage.teilweise',
            punkt: 'acht',
            tage_vergeben: vergeben.size,
            tage_ausserhalb: ausserhalb.size,
            rand,
            warum,
        };
    }
    if (weich.length > 0) {
        // Vormerkungen blocken nicht -- wer zuerst zusagt, bekommt sie. Aber wer
        // sie nicht sieht, verspricht zweimal dasselbe Geraet.
        warum.unshift({ key: 'lage.vorgemerkt', vormerkungen: weich.length });
        return {
            lage: 'geht',
            text_key: 'lage.vorgemerkt',
            punkt: 'acht',
            tage_vergeben: 0,
            // Hier ist nichts ausserhalb: Ein einziger Tag davon haette oben
            // 'teils' ergeben und waere nie bis hierher gekommen.
            tage_ausserhalb: 0,
            rand,
            warum,
        };
    }
    warum.unshift({ key: 'lage.frei', tage: fenster.length });
    return {
        lage: 'geht',
        text_key: 'lage.frei',
        punkt: 'gut',
        tage_vergeben: 0,
        tage_ausserhalb: 0,
        rand,
        warum,
    };
}
/**
 * Die Lage ueber mehrere Geraete eines Typs -- die Frage am Telefon lautet
 * nicht "ist RL-08 frei", sondern "habt ihr einen Radlader".
 *
 * `geht`, sobald ein Geraet im ganzen Fenster frei ist. `teils`, wenn keines
 * durchgehend frei ist, aber irgendwo Tage zu haben sind. `nein` erst, wenn
 * ueberall alles vergeben ist.
 *
 * **Und `menge` zaehlt** (P-22, Durchgang 03.10.2026): Wer drei Radlader
 * braucht und zwei frei sind, bekommt nicht „geht". Bis dahin stand bei
 * `menge=3` dieselbe Antwort wie bei `menge=2` -- `lage.frei`, zwei
 * Vorschlaege --, und wer darauf drei zusagte, hatte einen zu wenig. Jetzt:
 * `nein` mit `lage.zu_wenig {frei, menge}`; die freien bleiben als Vorschlag.
 */
export function lageUeberGeraete(befunde, menge = 1) {
    const frei = befunde.filter((b) => b.befund.lage === 'geht'
        && b.befund.tage_vergeben === 0
        // Folgt schon aus `lage === 'geht'` -- steht trotzdem da, weil dieser
        // Filter die Vorschlagsliste ist: Was hier hereinrutscht, bietet die
        // Maske als freies Geraet an.
        && b.befund.tage_ausserhalb === 0);
    const teils = befunde.filter((b) => b.befund.lage === 'teils');
    const warum = [
        { key: 'lage.geraete', gesamt: befunde.length, frei: frei.length, teils: teils.length },
    ];
    const gebraucht = Math.max(1, Math.floor(menge));
    if (frei.length >= gebraucht) {
        return { lage: 'geht', text_key: 'lage.frei', punkt: 'gut', frei: frei.map((b) => b.id), warum };
    }
    if (frei.length > 0) {
        return {
            lage: 'nein',
            text_key: 'lage.zu_wenig',
            punkt: 'stopp',
            frei: frei.map((b) => b.id),
            warum: [...warum, { key: 'lage.zu_wenig', frei: frei.length, menge: gebraucht }],
        };
    }
    if (teils.length > 0) {
        return {
            lage: 'teils',
            text_key: 'lage.teilweise',
            punkt: 'acht',
            frei: teils.map((b) => b.id),
            warum,
        };
    }
    return { lage: 'nein', text_key: 'lage.belegt', punkt: 'stopp', frei: [], warum };
}
//# sourceMappingURL=verfuegbarkeit.js.map